'use client';

import React from 'react';
import type { OfferCardData } from './types';
import PhoneIcon from '../Icons/PhoneIcon';
import { RichText } from '../RichText/RichText';
import { buttonBase, buttonSizes, buttonVariants } from '../Button/buttonStyles';
import IconTileGroup from '../IconTileGroup';
import type { PLPHeroBannerData } from '../PLPHeroBanner/types';

export interface PLPOfferCardProps {
  data: OfferCardData;
  onOpen: (offer: OfferCardData) => void;
  availabilityBadge?: string | null;
  offerTitleRichTextData?: React.ComponentProps<typeof RichText>['data'] | null;
  offerTeaserRichTextData?: React.ComponentProps<typeof RichText>['data'] | null;
  offerTitleText?: string | null;
  offerTeaserText?: string | null;
  callRailPhone?: string | null;
  phoneHref?: string | null;
  secondaryCtaLabel?: string | null;
  primaryCtaLabel?: string | null;
  iconTileGroup?: PLPHeroBannerData['iconTileGroup'];
}

const PLPOfferCard: React.FC<PLPOfferCardProps> = ({
  data,
  onOpen,
  availabilityBadge,
  offerTitleRichTextData,
  offerTitleText,
  offerTeaserRichTextData,
  offerTeaserText,
  callRailPhone,
  phoneHref,
  secondaryCtaLabel,
  primaryCtaLabel,
  iconTileGroup,
}) => {
  return (
    <article className='relative w-full overflow-hidden rounded-2xl bg-white shadow-xl [&&_div]:text-primary-500!'>
      <div className='flex h-full flex-col items-start gap-6 p-6'>
        {availabilityBadge ? (
          <div className='rounded-lg bg-[#F6F5F4] px-3 py-1'>
            <p className='flex items-center gap-3 font-instrument text-primary-500'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='8'
                height='8'
                viewBox='0 0 8 8'
                fill='none'
                aria-hidden='true'
              >
                <circle
                  cx='4'
                  cy='4'
                  r='4'
                  fill='#06DC06'
                />
              </svg>

              {availabilityBadge}
            </p>
          </div>
        ) : null}

        <div>
          {offerTitleRichTextData ? (
            <RichText data={offerTitleRichTextData} />
          ) : offerTitleText ? (
            <h2 className="mb-0! font-['Instrument_Sans'] text-[32px] font-bold leading-[110%] text-primary-500">
              {offerTitleText}
            </h2>
          ) : null}

          {offerTeaserRichTextData ? (
            <RichText
              data={{
                ...offerTeaserRichTextData,
                className: `
				[&_p]:m-0
        [&_p]:font-medium
        [&_p]:text-[18px]
        [&_p]:leading-[160%]
      `,
              }}
            />
          ) : offerTeaserText ? (
            <p className='font-medium text-[18px] leading-[160%]'>{offerTeaserText}</p>
          ) : null}
        </div>

        <button
          type='button'
          onClick={() => onOpen(data)}
          className='offer-button relative inline-block cursor-pointer text-xs text-primary-500/70 hover:underline focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:ring-offset-2'
        >
          See Terms & Conditions
        </button>

        <div className='mt-auto flex w-full flex-col gap-3'>
          {callRailPhone && phoneHref ? (
            <a
              href={phoneHref}
              className={`${buttonBase} ${buttonSizes.default} ${buttonVariants.navPrimary} w-full text-2xl`}
            >
              <PhoneIcon className='h-5 w-6 text-white-icon' />
              {secondaryCtaLabel || callRailPhone}
            </a>
          ) : null}

          <a
            href='#schedule'
            className='flex grow items-center justify-center rounded-lg border-2 border-secondary-500 px-8 py-4 font-semibold leading-6 text-primary-500 transition-all duration-200 hover:border-secondary-700 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-secondary-500/30 focus:ring-offset-2'
          >
            {primaryCtaLabel || 'Book Now'}
          </a>

          {/* Problem Tiles */}
          {iconTileGroup ? <IconTileGroup data={iconTileGroup} /> : null}
        </div>
      </div>
    </article>
  );
};

export default PLPOfferCard;
